import React, { createRef, useEffect, useState } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import { Theme } from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import cx from 'classnames'
import { useStickyState } from '../../hooks/useStickyState'

const MIN_WIDTH = 75

interface SplitViewProps {
  left: React.ReactElement
  right: React.ReactElement
  localStorageName: string
  className?: string
}

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    splitView: {
      display: 'flex',
    },
    divider: {
      height: '100%',
      border: '1px solid #808080',
    },
    dividerHitbox: {
      cursor: 'col-resize',
      alignSelf: 'stretch',
      display: 'flex',
      alignItems: 'center',
      padding: '0 0.3rem',
    },
    rightPanel: {
      flexGrow: 1,
    },
  })
)

const LeftPane: React.FunctionComponent<{
  leftWidth: number | undefined
  setLeftWidth: (value: number) => void
  children?: any
}> = ({ children, leftWidth, setLeftWidth }) => {
  const leftRef = createRef<HTMLDivElement>()

  useEffect(() => {
    if (leftRef.current) {
      if (!leftWidth) {
        setLeftWidth(leftRef.current.clientWidth)
        return
      }

      leftRef.current.style.width = `${leftWidth}px`
    }
  }, [leftRef, leftWidth, setLeftWidth])

  return <div ref={leftRef}>{children}</div>
}

export const SplitView: React.FunctionComponent<SplitViewProps> = ({
  left,
  right,
  className,
  localStorageName,
}) => {
  const [leftWidth, setLeftWidth] = useStickyState(window.innerWidth / 2, localStorageName)
  const [separatorXPosition, setSeparatorXPosition] = useState<undefined | number>(undefined)
  const [dragging, setDragging] = useState(false)

  const splitPaneRef = createRef<HTMLDivElement>()
  const classes = useStyles()

  const onMouseDown = (e: React.MouseEvent) => {
    setSeparatorXPosition(e.clientX)
    setDragging(true)
  }

  const onTouchStart = (e: React.TouchEvent) => {
    setSeparatorXPosition(e.touches[0].clientX)
    setDragging(true)
  }

  const onMove = (clientX: number) => {
    if (dragging && leftWidth && separatorXPosition) {
      const newLeftWidth = leftWidth + clientX - separatorXPosition
      setSeparatorXPosition(clientX)

      if (newLeftWidth < MIN_WIDTH) {
        setLeftWidth(MIN_WIDTH)
        return
      }

      if (splitPaneRef.current) {
        const splitPaneWidth = splitPaneRef.current.clientWidth

        if (newLeftWidth > splitPaneWidth - MIN_WIDTH) {
          setLeftWidth(splitPaneWidth - MIN_WIDTH)
          return
        }
      }

      setLeftWidth(newLeftWidth)
    }
  }

  const onMouseMove = (e: MouseEvent) => {
    onMove(e.clientX)
  }

  const onTouchMove = (e: TouchEvent) => {
    onMove(e.touches[0].clientX)
  }

  const onMouseUp = () => {
    setDragging(false)
  }

  React.useEffect(() => {
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('touchmove', onTouchMove)
    document.addEventListener('mouseup', onMouseUp)

    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  })

  return (
    <div className={cx(className, classes.splitView)} ref={splitPaneRef}>
      <LeftPane leftWidth={leftWidth} setLeftWidth={setLeftWidth}>
        {left}
      </LeftPane>
      <div
        className={classes.dividerHitbox}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchEnd={onMouseUp}
      >
        <div className={classes.divider} />
      </div>
      <div className={classes.rightPanel}>{right}</div>
    </div>
  )
}
